import { useCallback } from 'react';

import { normalizeInput, normalizeOutput, withName } from '../payload';

/**
 * The two halves of the module-builder agent's contract with the form.
 *
 * The chat panel runs on its own state; this is the only channel between it
 * and react-hook-form. On every send it snapshots what the user currently
 * sees, and mutations come back as a `partial` applied here — the agent never
 * touches the form directly.
 */
export const useAiModuleBridge = ({
  getValues,
  annotations,
  testResult,
  inputsArray,
  outputsArray,
  paramsArray,
  setValue,
  setTestResult,
  onRowsReplaced,
}) => {
  // Whatever the agent may write, it must also be able to read. `update_module`
  // merges its `tea` argument field-by-field against the tea object in this
  // snapshot (ai/module_builder/tools.py), so omitting it doesn't keep the
  // agent out of the Economics tab — it just means a partial TEA edit merges
  // into nothing and drops the fields it didn't mention. Same for
  // `allocatable`: the agent can set it, so it needs to see which byproducts
  // the user already marked non-allocatable before rewriting the array. And
  // for `name`: rows copied from a saved module keep theirs, which the agent
  // has to send back when it rewrites a list.
  const getFormSnapshot = useCallback(() => {
    const d = getValues();
    return {
      label: d.label,
      description: d.description,
      inputs: (d.inputs || []).map(withName(normalizeInput)),
      outputs: (d.outputs || []).map(withName(normalizeOutput)),
      params: (d.params || []).map(
        withName(p => ({
          label: p.label,
          type: p.type,
          unit: p.unit,
          default: p.default,
        })),
      ),
      balance: d.balance,
      tea: d.tea,
    };
  }, [getValues]);

  const getAnnotations = useCallback(() => annotations, [annotations]);
  const getLastTestResult = useCallback(() => testResult, [testResult]);

  const onModuleUpdate = useCallback(
    partial => {
      // Apply each slot via setValue so react-hook-form's live watchers
      // pick it up. Arrays must be replaced wholesale (useFieldArray's
      // .replace ensures the field IDs are regenerated).
      //
      // `tea` lands in the `else` branch — that's intentional. The server
      // pre-merges TEA partials against the snapshot above before emitting the
      // mirror event, so `value` is the fully-merged TEA object. setValue
      // replaces the form's tea state with this merged value in one shot;
      // nested register('tea.x') bindings re-read from the new state.
      //
      // `onRowsReplaced` hears about each rewritten IO list before it lands,
      // so state kept per row (the species-change baseline) can follow rows
      // the agent moved rather than stay with their old positions.
      Object.entries(partial).forEach(([key, value]) => {
        if (key === 'inputs') {
          onRowsReplaced('inputs', getValues('inputs'), value);
          inputsArray.replace(value);
        } else if (key === 'outputs') {
          // Normalize here too: the agent can emit an emissions output that
          // is also flagged allocatable, and this path writes straight into
          // the form without passing through a payload builder.
          const outputs = (value ?? []).map(withName(normalizeOutput));
          onRowsReplaced('outputs', getValues('outputs'), outputs);
          outputsArray.replace(outputs);
        } else if (key === 'params') {
          paramsArray.replace(value);
        } else {
          setValue(key, value, { shouldDirty: true });
        }
      });
    },
    [getValues, inputsArray, outputsArray, paramsArray, setValue, onRowsReplaced],
  );

  const onTestResult = useCallback(result => setTestResult(result), [setTestResult]);

  return { getFormSnapshot, getAnnotations, getLastTestResult, onModuleUpdate, onTestResult };
};
